import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import useRequiredFields from "../../../../hooks/useRequiredFields";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";

const Edit = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const { id } = useParams();
  const { checkRequiredFields } = useRequiredFields();
  const [loading, setLoading] = useState(false);
  const [finishedGoods, setFinishedGoods] = useState([]);
  const [selectedFinishedGoods, setSelectedFinishedGoods] = useState([]);
  const [productionStaff, setProductionStaff] = useState([]);
  const [selectedProductionStaff, setSelectedProductionStaff] = useState([]);
  const [unit, setUnit] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const normalizeOptions = (items) => {
    if (!Array.isArray(items)) return [];

    return items.map((item) => ({
      ...item,
      value: item.value ?? item.id,
      label: item.label ?? item.name ?? String(item.id),
    }));
  };

  const parseSelectedIds = (data) => {
    if (!data) return [];

    // matches your response format: group_items: [{ product_id: ... }]
    if (Array.isArray(data.group_items)) {
      return data.group_items
        .map((item) => Number(item?.product_id))
        .filter((v) => !Number.isNaN(v));
    }

    if (Array.isArray(data.product_ids)) {
      return data.product_ids.map((v) => Number(v));
    }

    if (typeof data.product_ids === "string") {
      return data.product_ids
        .split(",")
        .map((v) => Number(v.trim()))
        .filter((v) => !Number.isNaN(v));
    }

    if (data.product_id) {
      const productId = Number(data.product_id);
      return Number.isNaN(productId) ? [] : [productId];
    }

    return [];
  };

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        const [productRes, groupRes, staffRes, unitRes] = await Promise.all([
          // Http.get("/product-list/finished_goods?product_group=true"),
          Http.get("/restaurant-finished-good"),
          Http.get("/product-group/" + id),
          Http.get("/staff2?user_type=production"),
          Http.get("/active-unit"),
        ]);

        const options = normalizeOptions(productRes.data);
        const staffOptions = normalizeOptions(staffRes.data);
        const unitOptions = normalizeOptions(unitRes.data);
        const selectedIds = parseSelectedIds(groupRes.data);
        const preSelected = options.filter((item) =>
          selectedIds.includes(Number(item.value)),
        );

        const selectedStaffIds = Array.isArray(groupRes.data.production_staff)
          ? groupRes.data.production_staff
              .map((item) => Number(item?.production_id))
              .filter((v) => !Number.isNaN(v))
          : Array.isArray(groupRes.data.staff_ids)
          ? groupRes.data.staff_ids.map((v) => Number(v))
          : [];
        const preSelectedStaff = staffOptions.filter((item) =>
          selectedStaffIds.includes(Number(item.value)),
        );

        const currentUnitId = Number(
          groupRes.data?.group_items?.[0]?.unit_id ?? groupRes.data?.unit?.id ?? "",
        );
        const preSelectedUnit =
          unitOptions.find((item) => Number(item.value) === currentUnitId) || null;

        setFinishedGoods(options);
        setSelectedFinishedGoods(preSelected);
        setProductionStaff(staffOptions);
        setSelectedProductionStaff(preSelectedStaff);
        setUnit(unitOptions);
        setSelectedUnit(preSelectedUnit);
        setInputs({
          ...groupRes.data,
          name: groupRes.data?.name || "",
          status: groupRes.data?.status || "active",
          product_ids: selectedIds,
          // keep first selected id for backward compatibility
          product_id: selectedIds.length > 0 ? selectedIds[0] : "",
          staff_ids: selectedStaffIds,
          production_staff: selectedStaffIds,
          unit_id: preSelectedUnit ? preSelectedUnit.value : "",
        });
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleFinishedGoodsChange = (selectedOptions) => {
    const selectedItems = selectedOptions || [];
    const selectedIds = selectedItems.map((item) => item.value);

    setSelectedFinishedGoods(selectedItems);
    setInputs((values) => ({
      ...values,
      product_ids: selectedIds,
      product_id: selectedIds.length > 0 ? selectedIds[0] : "",
    }));

    setError((prev) => ({
      ...prev,
      product_id: null,
      product_ids: null,
    }));
  };

  const handleProductionStaffChange = (selectedOptions) => {
    const selectedItems = selectedOptions || [];
    const selectedIds = selectedItems.map((item) => item.value);

    setSelectedProductionStaff(selectedItems);
    setInputs((values) => ({
      ...values,
      staff_ids: selectedIds,
      production_staff: selectedIds,
    }));

    setError((prev) => ({
      ...prev,
      staff_ids: null,
    }));
  };

  const unitLoad = (selectedOption) => {
    setSelectedUnit(selectedOption);

    setInputs((values) => ({
      ...values,
      unit_id: selectedOption ? selectedOption.value : "",
    }));

    setError((prev) => ({
      ...prev,
      unit_id: null,
    }));
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small">{error[fieldName]}</div>;
    }
    return null;
  };

  const formSubmit = () => {
    if (checkRequiredFields()) {
      if (!inputs.product_ids || inputs.product_ids.length === 0) {
        setError((prev) => ({
          ...prev,
          product_id: "Finished Good field is required.",
        }));
        return;
      }

      if (!inputs.unit_id) {
        setError((prev) => ({
          ...prev,
          unit_id: "Units field is required.",
        }));
        return;
      }

      Http.put("/product-group/" + id, inputs)
        .then((res) => {
          navigate("/product-group", {
            state: { message: res.data.message, type: "success" },
          });
        })
        .catch(function (e) {
          setError(e.response.data.errors);
        });
    }
  };

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Edit Product Group</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Edit Product Group Form</h6>

            {loading ? (
              <ProgressSpinner />
            ) : (
              <div>
                <div className="row">
                  <div className="col-lg">
                    <FormValidation error={error} />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg">
                    <label className="font-weight-bold">
                      Name <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={inputs.name || ""}
                      onChange={handleChange}
                      placeholder="Input Name"
                      data-required
                    />
                    {displayError("name")}
                  </div>

                  <div className="col-lg">
                    <label htmlFor="">
                      Finished Good <i className="fa fa-star required"></i>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      value={selectedFinishedGoods}
                      name="product_id"
                      options={finishedGoods}
                      isMulti={true}
                      closeMenuOnSelect={false}
                      placeholder="Select Finished Goods"
                      onChange={handleFinishedGoodsChange}
                    />
                    {displayError("product_id")}
                  </div>

                  <div className="col-lg">
                    <label htmlFor="">
                      Units <i className="fa fa-star required"></i>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      value={selectedUnit}
                      name="unit_id"
                      options={unit}
                      isMulti={false}
                      closeMenuOnSelect={true}
                      placeholder="Select Unit"
                      onChange={unitLoad}
                    />
                    {displayError("unit_id")}
                  </div>

                  <div className="col-lg">
                    <label htmlFor="">
                      Select Production <i className="fa fa-star required"></i>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      value={selectedProductionStaff}
                      name="staff_ids"
                      options={productionStaff}
                      isMulti={true}
                      closeMenuOnSelect={false}
                      placeholder="Select Production Staff"
                      onChange={handleProductionStaffChange}
                    />
                    {displayError("staff_ids")}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-3">
                    <button className="btn btn-primary btn-block mg-b-10" onClick={formSubmit}>
                      <i className="fa fa-send mg-r-10"></i> Update
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* card */}
        </div>
      </div>
    </div>
  );
};

export default Edit;
